import React, { useState, useEffect } from 'react';
import { projectsApi } from '../services/api';
import { toast } from 'sonner';
import { Link2, Copy, RefreshCw, Trash2, Globe, Lock, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isValidEmail } from '../lib/validation';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalClose,
  ResponsiveModalBody,
} from './ui/responsive-modal';

const ShareProjectModal = ({ isOpen, onClose, project }) => {
  const { t } = useTranslation();
  const [shareToken, setShareToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);

  const projectId = project?.id;
  useEffect(() => {
    if (!isOpen || !projectId) return;
    setLoading(true);
    projectsApi.getById(projectId)
      .then(res => setShareToken(res.data?.share_token || null))
      .catch(() => setShareToken(null))
      .finally(() => setLoading(false));
  }, [isOpen, projectId]);

  if (!project) return null;

  const shareUrl = shareToken
    ? `${window.location.origin}/share/${shareToken}`
    : null;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await projectsApi.share(project.id);
      setShareToken(res.data.share_token);
      toast.success(t('projects.share_link_generated'));
    } catch {
      toast.error(t('projects.share_link_generate_failed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await projectsApi.revokeShare(project.id);
      setShareToken(null);
      toast.success(t('projects.share_link_revoked'));
    } catch {
      toast.error(t('projects.share_link_revoke_failed'));
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    toast.success(t('projects.share_link_copied'));
  };

  const handleRegenerate = async () => {
    setGenerating(true);
    try {
      const res = await projectsApi.share(project.id);
      setShareToken(res.data.share_token);
      toast.success(t('projects.share_link_regenerated'));
    } catch {
      toast.error(t('projects.share_link_regenerate_failed'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <ResponsiveModal open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <ResponsiveModalContent className="bg-popover border-border md:rounded-xl">
        <ResponsiveModalHeader className="border-border">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: project.color || '#6B7280' }} />
          <ResponsiveModalTitle className="text-base text-foreground">
            {t('projects.share_project_title', { name: project.name })}
          </ResponsiveModalTitle>
          <ResponsiveModalClose className="text-muted-foreground hover:bg-transparent hover:text-foreground" />
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
            </div>
          ) : shareToken ? (
            /* - Active share link - */
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Globe className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-300">
                  {t('projects.share_link_public_notice')}
                </p>
              </div>

              {/* Link box */}
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('projects.share_link_label')}</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 bg-accent/60 border border-border rounded-lg px-3 py-2 text-sm text-foreground/80 truncate focus:outline-none"
                  />
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-black text-sm font-medium rounded-lg hover:bg-amber-400 transition-colors flex-shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {t('common.copy')}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={handleRegenerate}
                  disabled={generating}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground/80 transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${generating ? 'animate-spin' : ''}`} />
                  {t('projects.share_regenerate_link')}
                </button>
                <button
                  onClick={handleRevoke}
                  disabled={revoking}
                  className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {revoking ? t('projects.share_revoking') : t('projects.share_revoke_link')}
                </button>
              </div>
            </div>
          ) : (
            /* - No share link yet - */
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-accent/60 border border-border rounded-lg">
                <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  {t('projects.share_private_notice')}
                </p>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                <Link2 className="w-4 h-4" />
                {generating ? t('projects.share_generating') : t('projects.share_generate_link')}
              </button>
            </div>
          )}

          {/* Email - always visible */}
          {!loading && (
            <div className="space-y-2 pt-1 border-t border-border">
              <label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('projects.share_send_via_email')}</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="recipient@example.com"
                  value={emailInput}
                  onChange={e => { setEmailInput(e.target.value); if (emailError) setEmailError(''); }}
                  className={`flex-1 bg-accent/60 border rounded-lg px-3 py-2 text-sm text-foreground/80 placeholder:text-muted-foreground focus:outline-none ${emailError ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-amber-500'}`}
                />
                <button
                  disabled={sending}
                  onClick={async () => {
                    if (!isValidEmail(emailInput)) {
                      setEmailError(t('auth.invalid_email'));
                      return;
                    }
                    setEmailError('');
                    setSending(true);
                    try {
                      let url = shareUrl;
                      if (!url) {
                        const res = await projectsApi.share(project.id);
                        setShareToken(res.data.share_token);
                        url = `${window.location.origin}/share/${res.data.share_token}`;
                      }
                      await projectsApi.sendShareEmail(project.id, emailInput.trim(), url);
                      toast.success(t('projects.share_invite_sent', { email: emailInput.trim() }));
                      setEmailInput('');
                    } catch {
                      toast.error(t('projects.share_email_failed'));
                    } finally {
                      setSending(false);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-accent text-foreground/80 text-sm font-medium rounded-lg hover:bg-accent transition-colors flex-shrink-0 disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                  {sending ? t('common.sending') : t('common.send')}
                </button>
              </div>
              {emailError && <p className="text-xs text-red-400">{emailError}</p>}
            </div>
          )}
        </ResponsiveModalBody>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};

export default ShareProjectModal;
